import React, { PropsWithChildren, ReactNode, useRef, useState } from 'react'

import { IQuestionModel, QuestionType } from '../../types'

import LoaderView from '../../../actions/loader/View'
import { IImageAsset } from '../../../modules/images/types'
import QuestionTitle from '../QuestionTitle/QuestionTitle'
import useImages from '../../../modules/images/useImages'
import useTimeline from '../../../modules/timeline/useTimeline'
import { useTimelineFrameInteractionStart } from '../../../modules/timeline/useTimelineFrame'
import useViewport from '../../../modules/viewport/useViewport'
import useSession from '../../../session/useSession'
import useDetectFrameFullscreenHeight from './useDetectFrameFullscreenHeight'
import useQuestionFramePreloadImages from './useQuestionFramePreloadImages'

import style from './style.module.scss'
import commonStyle from '../style.module.scss'
import TextWithOptions from '@aytm/app/swidget/base/modules/preview/components/TextWithOptions'
import usePreviewOptions from '@aytm/app/swidget/base/modules/preview/usePreviewOptions'
import imageAssetWithOptions from '@aytm/app/swidget/base/modules/preview/functions/imageAssetWithOptions'
import { IMAGE_ASSET_SIZE_MIDDLE } from '@aytm/lib/common/assets.ts'

export interface QuestionFrameProps {
  model: IQuestionModel
  titleText?: ReactNode | string
  titleImage?: IImageAsset
  collapsibleTitle?: boolean
  images?: string[]
  // videoEmbedding?: IVideoInternalAsset // for radio buttons and sliders
  fullsize?: boolean
  noImage?: boolean
  noTitle?: boolean
  bodyClassName?: string
  onFrameFullscreenHeight?: (height: number) => void
}

export default function QuestionFrame({
  model,
  titleText,
  titleImage,
  collapsibleTitle,
  images,
  noImage,
  noTitle,
  fullsize,
  bodyClassName,
  onFrameFullscreenHeight,
  children
}: PropsWithChildren<QuestionFrameProps>) {
  const { schema, frame, navigationFrame } = model

  const session = useSession()
  const timeline = useTimeline()
  const viewport = useViewport()
  const previewOptions = usePreviewOptions()
  const frameInteractionStart = useTimelineFrameInteractionStart(timeline, frame)

  const { ImageAsset } = useImages()

  const isLoaded = useQuestionFramePreloadImages(frameInteractionStart, images, schema)
  const image =
    schema.type == QuestionType.IMAGE ||
    schema.type == QuestionType.HEATMAP ||
    schema.type == QuestionType.RAPID_ASSOCIATION ||
    schema.type == QuestionType.AD_TESTING ||
    schema.type == QuestionType.MENU
      ? null
      : imageAssetWithOptions(session.rules?.sub?.[schema.id]?.[schema.id], titleImage || schema.image)

  const text = 'undefined' === typeof titleText ? schema.text : titleText

  const questionTitle =
    'string' == typeof text ? (
      previewOptions.showHidden || !session.rules?.hide_text?.[schema.id]?.condition ? (
        <TextWithOptions text={text} options={previewOptions} rules={session.rules?.sub?.[schema.id]?.[schema.id]} />
      ) : null
    ) : (
      titleText
    )

  const isQuestionImageExpanded = Boolean(image?.expand)
  const [isQuestionExpandedImageLoaded, setIsQuestionExpandedImageLoaded] = useState<boolean>(!isQuestionImageExpanded)
  const onQuestionExpandedImageLoadedRef = useRef<() => void>()
  if (null == onQuestionExpandedImageLoadedRef.current) {
    onQuestionExpandedImageLoadedRef.current = () =>
      void (setIsQuestionExpandedImageLoaded(true), frameInteractionStart())
  }

  // log click on embedded video for radio buttons and sliders
  const innerElementRef = useRef<HTMLDivElement>()
  useDetectFrameFullscreenHeight(
    onFrameFullscreenHeight,
    innerElementRef,
    session,
    viewport,
    isLoaded && isQuestionExpandedImageLoaded
  )

  return (
    <>
      {isLoaded ? (
        <>
          {!noTitle && (
            <QuestionTitle
              title={questionTitle}
              image={noImage ? null : image}
              schema={schema}
              frame={navigationFrame ? navigationFrame[0] : null}
              collapsible={collapsibleTitle}
            />
          )}
          <div
            className={`${style.body}${fullsize ? ' ' + style.fullsize : ''} ${
              viewport.mobile ? ' ' + commonStyle.mobile : ''
            }${bodyClassName ? ' ' + bodyClassName : ''} ${style[schema.type] || ''}`}
            data-testid="SW-Question-Body"
          >
            {isQuestionImageExpanded ? (
              <div className={style.expandedImage} data-testid="SW-Question-ExpandedImage">
                <ImageAsset
                  className={commonStyle.image}
                  width={viewport.mobile ? viewport.width : (viewport.widgetWidth as number)}
                  height={IMAGE_ASSET_SIZE_MIDDLE}
                  asset={image}
                  size="medium"
                  onFullImageLoaded={onQuestionExpandedImageLoadedRef.current}
                  previewLabelTemplate={questionTitle}
                />
              </div>
            ) : null}

            <div
              className={`${style.inner} ${style[schema.type] || ''} `}
              ref={innerElementRef}
              key="QuestionFrameInner"
              data-testid="SW-Question-Text"
            >
              {children}
            </div>
          </div>
        </>
      ) : (
        <LoaderView />
      )}
    </>
  )
}
